import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import subscriptionPlanService from '../services/subscriptionPlanService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const Subscription = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form and dialog state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [razorpayPlanId, setRazorpayPlanId] = useState('');
  const [planFetchedFromRazorpay, setPlanFetchedFromRazorpay] = useState(false);
  const [fetchingRazorpay, runFetchRazorpay] = useActionLock();
  const [formData, setFormData] = useState({
    plan_name: '',
    plan_code: '',
    price: '',
    subscription_billing_cycle: '',
    trial_days: '',
    description: '',
    max_assets: '',
    max_nominees: '',
    max_storage_gb: '',
    is_active: false,
  });

  // API state
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, runSave] = useActionLock();
  const [deleting, runDelete] = useActionLock();
  const [syncing, runSync] = useActionLock();
  const [toggling, setToggling] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const hasFetched = useRef(false);

  // Fetch plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;

      setLoading(true);
      setError('');

      const result = await subscriptionPlanService.getPlans();

      if (result.success) {
        setPlans(result.data.results || result.data || []);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    fetchPlans();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const refreshPlans = async () => {
    const result = await subscriptionPlanService.getPlans();
    if (result.success) {
      setPlans(result.data.results || result.data || []);
    }
  };

  // Helper to get display name for billing cycle
  const getBillingCycleDisplay = (cycle) => {
    const cycleMap = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      half_yearly: 'Half Yearly',
      yearly: 'Yearly',
    };
    return cycleMap[cycle] || cycle;
  };

  const handleAddNew = () => {
    setFormData({
      plan_name: '',
      plan_code: '',
      price: '',
      subscription_billing_cycle: '',
      trial_days: '',
      description: '',
      max_assets: '',
      max_nominees: '',
      max_storage_gb: '',
      is_active: false,
    });
    setRazorpayPlanId('');
    setPlanFetchedFromRazorpay(false);
    setEditingPlan(null);
    setShowAddForm(true);
  };

  const handleFetchRazorpayPlan = async () => {
    const id = (razorpayPlanId || '').trim();
    if (!id) {
      showSnackbar('Please enter Razorpay Plan ID', 'error');
      return;
    }
    try {
      const result = await subscriptionPlanService.fetchRazorpayPlan(id);
      if (result.success && result.data) {
        const d = result.data;
        setFormData({
          plan_name: d.plan_name ?? d.name ?? '',
          plan_code: d.plan_code ?? d.plan_id ?? d.id ?? '',
          price: d.price ?? d.amount ?? '',
          subscription_billing_cycle: (d.subscription_billing_cycle ?? (d.period === 'monthly' ? 'monthly' : d.period === 'yearly' ? 'yearly' : '')) || '',
          trial_days: d.trial_days ?? d.trial_period_days ?? '',
          description: d.description ?? '',
          max_assets: d.max_assets ?? '',
          max_nominees: d.max_nominees ?? '',
          max_storage_gb: d.max_storage_gb ?? '',
          is_active: d.is_active ?? false,
        });
        setPlanFetchedFromRazorpay(true);
        showSnackbar('Plan details fetched successfully');
      } else {
        showSnackbar(result.error || 'Failed to fetch plan', 'error');
      }
    } catch (err) {
      showSnackbar('Failed to fetch Razorpay plan', 'error');
    }
  };

  const showPlanDetailsForm = editingPlan || planFetchedFromRazorpay;

  const handleEdit = (plan) => {
    setFormData({
      plan_name: plan.plan_name,
      plan_code: plan.plan_code,
      price: plan.price,
      subscription_billing_cycle: plan.subscription_billing_cycle,
      trial_days: plan.trial_days,
      description: plan.description ?? plan.plan_description ?? '',
      max_assets: plan.max_assets,
      max_nominees: plan.max_nominees,
      max_storage_gb: plan.max_storage_gb,
      is_active: plan.is_active,
    });
    setEditingPlan(plan);
    setShowAddForm(true);
  };

  const isEditMode = !!editingPlan;

  const handleSync = async (plan) => {
    try {
      const result = await subscriptionPlanService.syncFromRazorpay(plan.plan_id);
      if (result.success) {
        showSnackbar('Plan synced from Razorpay successfully');
        await refreshPlans();
        if (editingPlan && editingPlan.plan_id === plan.plan_id && result.data) {
          const d = result.data;
          setFormData((prev) => ({
            ...prev,
            plan_name: d.plan_name ?? d.name ?? prev.plan_name,
            price: d.price ?? d.amount ?? prev.price,
            subscription_billing_cycle: (d.subscription_billing_cycle ?? (d.period === 'monthly' ? 'monthly' : d.period === 'yearly' ? 'yearly' : '')) || prev.subscription_billing_cycle,
            description: d.description ?? prev.description,
          }));
        }
      } else {
        showSnackbar(result.error || 'Failed to sync plan', 'error');
      }
    } catch (err) {
      showSnackbar('Failed to sync plan from Razorpay', 'error');
    }
  };

  const handleDelete = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await subscriptionPlanService.deletePlan(planToDelete.plan_id);

      if (result.success) {
        showSnackbar('Subscription plan deleted successfully');
        await refreshPlans();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while deleting', 'error');
    } finally {
      setShowDeleteDialog(false);
      setPlanToDelete(null);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (editingPlan) {
      // Update: validate form fields
      if (!formData.plan_name.trim()) {
        showSnackbar('Please enter plan name', 'error');
        return;
      }
      if (!formData.price || parseFloat(formData.price) <= 0) {
        showSnackbar('Please enter a valid price (greater than 0)', 'error');
        return;
      }
      if (!formData.subscription_billing_cycle) {
        showSnackbar('Please select billing cycle', 'error');
        return;
      }
    } else {
      // Create: only razorpay_plan_id is required
      if (!razorpayPlanId || !razorpayPlanId.trim()) {
        showSnackbar('Razorpay Plan ID is required to create a plan', 'error');
        return;
      }
    }

    try {
      let result;
      if (editingPlan) {
        const payload = {
          plan_name: formData.plan_name.trim(),
          price: parseFloat(formData.price),
          subscription_billing_cycle: formData.subscription_billing_cycle,
          description: (formData.description || '').trim(),
        };
        result = await subscriptionPlanService.updatePlan(editingPlan.plan_id, payload);
      } else {
        const payload = { razorpay_plan_id: razorpayPlanId.trim() };
        result = await subscriptionPlanService.createPlan(payload);
      }

      if (result.success) {
        showSnackbar(editingPlan ? 'Plan updated successfully' : 'Plan created successfully');
        await refreshPlans();
        setShowAddForm(false);
        setEditingPlan(null);
        if (!editingPlan) {
          setRazorpayPlanId('');
          setPlanFetchedFromRazorpay(false);
        }
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while saving', 'error');
    }
  };

  const handleToggleStatus = async (plan) => {
    setToggling(plan.plan_id);

    try {
      const result = await subscriptionPlanService.toggleActive(plan.plan_id);

      if (result.success) {
        showSnackbar(`Plan ${result.data.is_active ? 'activated' : 'deactivated'} successfully`);
        await refreshPlans();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while toggling status', 'error');
    } finally {
      setToggling(null);
    }
  };

  const handleExport = () => {
    console.log('Exporting to Excel...');
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Manage Subscription
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Add New Plan
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Export as Excel
            </Button>
          </Box>
        </Box>
      </Paper>



      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid #ECF0F1',
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 900, sm: 'auto' } }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    subscription_plan_id
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    subscription_plan_name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    billing_cycle
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    price
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    Plan_status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      padding: '14px',
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan, index) => (
                  <TableRow
                    key={plan.plan_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#ECF0F1',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '14px',
                      }}
                    >
                      {plan.plan_id}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '14px',
                      }}
                    >
                      {plan.plan_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '14px',
                      }}
                    >
                      {getBillingCycleDisplay(plan.subscription_billing_cycle)}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '14px',
                      }}
                    >
                      {plan.price}
                    </TableCell>
                    <TableCell sx={{ padding: '14px' }}>
                      <Chip
                        label={plan.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: plan.is_active ? '#D4F4DD' : '#FEE2E2',
                          color: plan.is_active ? '#059669' : '#DC2626',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '22px',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '14px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Switch
                          checked={plan.is_active}
                          onChange={() => handleToggleStatus(plan)}
                          disabled={toggling === plan.plan_id}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4E83BD',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4E83BD',
                            },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(plan)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(plan)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {plans.length === 0 && (
            <Box sx={{ padding: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Subscription Plans Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first subscription plan
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add New Plan
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {plans.map((plan, index) => (
            <Paper
              key={plan.plan_id}
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #ECF0F1',
                backgroundColor: '#FFFFFF',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  backgroundColor: '#4E83BD',
                  padding: '10px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Chip
                  label={plan.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    backgroundColor: plan.is_active ? '#D4F4DD' : '#FEE2E2',
                    color: plan.is_active ? '#059669' : '#DC2626',
                    fontWeight: 600,
                    fontSize: '11px',
                    height: '22px',
                  }}
                />
                <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>
                  {getBillingCycleDisplay(plan.subscription_billing_cycle)} • ₹{plan.price}
                </Typography>
              </Box>

              {/* Card Content */}
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748B',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Plan Name
                </Typography>
                <Typography
                  sx={{
                    color: '#2C3E50',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: 1.5,
                  }}
                >
                  {plan.plan_name}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}
                    >
                      Billing
                    </Typography>
                    <Typography sx={{ color: '#2C3E50', fontSize: '14px', fontWeight: 500 }}>
                      {getBillingCycleDisplay(plan.subscription_billing_cycle)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}
                    >
                      Price
                    </Typography>
                    <Typography sx={{ color: '#2C3E50', fontSize: '14px', fontWeight: 600 }}>
                      ₹{plan.price}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Card Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '8px 16px',
                  borderTop: '1px solid #ECF0F1',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '12px', color: '#64748B' }}>Active</Typography>
                  <Switch
                    checked={plan.is_active}
                    onChange={() => handleToggleStatus(plan)}
                    disabled={toggling === plan.plan_id}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4E83BD' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4E83BD' },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(plan)}
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(plan)}
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}

          {/* Empty State for Mobile */}
          {plans.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                padding: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Subscription Plans Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first subscription plan
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': { backgroundColor: '#3A6A99' },
                }}
              >
                Add New Plan
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {/* Add/Edit Plan Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setEditingPlan(null);
          setPlanFetchedFromRazorpay(false);
          setRazorpayPlanId('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '18px' }}>
            {editingPlan ? 'Edit Subscription Plan' : 'Add New Subscription Plan'}
          </Typography>
          <IconButton
            onClick={() => {
              setShowAddForm(false);
              setEditingPlan(null);
              setPlanFetchedFromRazorpay(false);
              setRazorpayPlanId('');
            }}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          {!editingPlan && !showPlanDetailsForm ? (
            /* Step 1: Add New Plan - only Razorpay ID + Fetch */
            <Box sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                  Fetch plan from Razorpay
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#64748B', marginBottom: 2 }}>
                Enter the Razorpay Plan ID (e.g. plan_xxxx) and click Fetch to load plan details.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="Razorpay Plan ID"
                  placeholder="e.g., plan_xxxxxxxxxxxx"
                  value={razorpayPlanId}
                  onChange={(e) => setRazorpayPlanId(e.target.value)}
                  disabled={fetchingRazorpay}
                  sx={{
                    flex: 1,
                    minWidth: 240,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#4E83BD' },
                      '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                    },
                  }}
                />
                <ActionButton
                  variant="contained"
                  loading={fetchingRazorpay}
                  onClick={() => runFetchRazorpay(handleFetchRazorpayPlan)}
                  loadingText="Fetching..."
                  sx={{
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    fontWeight: 600,
                    padding: '14px 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    minHeight: 56,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3A6A99 0%, #2D5480 100%)',
                    },
                  }}
                >
                  Fetch
                </ActionButton>
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingPlan(null);
                  setPlanFetchedFromRazorpay(false);
                  setRazorpayPlanId('');
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  color: '#64748B',
                  borderColor: '#E5E7EB',
                  marginTop: 2,
                  '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
                }}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            /* Step 2: Full form (after fetch or when editing) */
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Left Section - Plan Details */}
              <Box sx={{
                flex: 1,
                padding: 3,
                borderRight: { md: '1px solid #E5E7EB' },
                borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                    Plan Details
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Plan Name - full width */}
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Plan Name <span style={{ color: '#DC2626' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Economy Pack"
                      value={formData.plan_name}
                      onChange={(e) => handleFormChange('plan_name', e.target.value)}
                      disabled={isEditMode || planFetchedFromRazorpay}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: (isEditMode || planFetchedFromRazorpay) ? '#EEF2F7' : '#F8FAFC',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor: (isEditMode || planFetchedFromRazorpay) ? '#E5E7EB' : '#4E83BD' },
                          '&.Mui-focused fieldset': { borderColor: '#4E83BD', borderWidth: '1px' },
                        },
                        '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                      }}
                    />
                  </Box>

                  {/* Price & Billing Cycle - same row */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Price (₹) <span style={{ color: '#DC2626' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="e.g., 199"
                        value={formData.price}
                        onChange={(e) => handleFormChange('price', e.target.value)}
                        disabled={isEditMode || planFetchedFromRazorpay}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: (isEditMode || planFetchedFromRazorpay) ? '#EEF2F7' : '#F8FAFC',
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: (isEditMode || planFetchedFromRazorpay) ? '#E5E7EB' : '#4E83BD' },
                            '&.Mui-focused fieldset': { borderColor: '#4E83BD', borderWidth: '1px' },
                          },
                          '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Billing Cycle <span style={{ color: '#DC2626' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={formData.subscription_billing_cycle}
                        onChange={(e) => handleFormChange('subscription_billing_cycle', e.target.value)}
                        disabled={isEditMode || planFetchedFromRazorpay}
                        SelectProps={{
                          IconComponent: KeyboardArrowDownIcon,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor:(isEditMode || planFetchedFromRazorpay) ? '#EEF2F7' : '#F8FAFC',
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: (isEditMode || planFetchedFromRazorpay) ? '#E5E7EB' : '#4E83BD' },
                            '&.Mui-focused fieldset': { borderColor: '#4E83BD', borderWidth: '1px' },
                          },
                          '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                        }}
                      >
                        <MenuItem value="">Select billing cycle</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Description - full width, multiline */}
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Description
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      maxRows={5}
                      placeholder="e.g., Premium Yearly - Unlimited assets, auto-debit recurring"
                      value={formData.description ?? ''}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      disabled={isEditMode || planFetchedFromRazorpay}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor:(isEditMode || planFetchedFromRazorpay) ? '#EEF2F7' : '#F8FAFC',
                          alignItems: 'flex-start',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor:(isEditMode || planFetchedFromRazorpay) ? '#E5E7EB' : '#4E83BD' },
                          '&.Mui-focused fieldset': { borderColor: '#4E83BD', borderWidth: '1px' },
                        },
                        '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B', py: 1.25 },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Right Section - Actions */}
              <Box sx={{
                flex: 1,
                padding: 3,
                backgroundColor: '#FFFFFF',
              }}>
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, marginTop: 4 }}>
                  {editingPlan && (
                    <ActionButton
                      variant="outlined"
                      fullWidth
                      loading={syncing}
                      onClick={() => runSync(() => handleSync(editingPlan))}
                      loadingText="Syncing..."
                      startIcon={<SyncIcon />}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        color: '#4E83BD',
                        borderColor: '#4E83BD',
                        padding: '12px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#3A6A99',
                          backgroundColor: 'rgba(78, 131, 189, 0.08)',
                        },
                      }}
                    >
                      Sync from Razorpay
                    </ActionButton>
                  )}
                  {!editingPlan && (
                    <ActionButton
                      variant="contained"
                      fullWidth
                      loading={saving}
                      onClick={() => runSave(handleSave)}
                      loadingText="Saving..."
                      sx={{
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                        fontWeight: 600,
                        padding: '14px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #3A6A99 0%, #2D5480 100%)',
                          boxShadow: '0 6px 16px rgba(78, 131, 189, 0.4)',
                        },
                      }}
                    >
                      Create Plan
                    </ActionButton>
                  )}
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingPlan(null);
                      setPlanFetchedFromRazorpay(false);
                      setRazorpayPlanId('');
                    }}
                    disabled={saving}
                    sx={{
                      textTransform: 'none',
                      fontSize: '14px',
                      color: '#64748B',
                      borderColor: '#E5E7EB',
                      padding: '12px',
                      borderRadius: '10px',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#CBD5E1',
                        backgroundColor: '#F8FAFC',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#2C3E50',
              marginBottom: 2,
            }}
          >
            Confirm Delete
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#6B7280',
              lineHeight: 1.6,
              marginBottom: 3,
            }}
          >
            Are you sure you want to delete the plan <strong>"{planToDelete?.plan_name}"</strong>? This action cannot be undone.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: '#6B7280',
                borderColor: '#D1D5DB',
                paddingX: 3,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="contained"
              loading={deleting}
              onClick={() => runDelete(handleConfirmDelete)}
              loadingText="Deleting..."
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                backgroundColor: '#DC2626',
                color: 'white',
                paddingX: 3,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#B91C1C',
                },
              }}
            >
              Yes
            </ActionButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Subscription;